import SalviaLayout from "@/components/SalviaLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import {
  ClipboardList,
  Clock,
  PlayCircle,
  CheckCircle2,
  Cog,
  Search,
  Loader2,
  AlertCircle,
  Inbox,
  Eye,
  X,
} from "lucide-react";
import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { useLocation } from "wouter";

import { usePageTitle } from "@/hooks/usePageTitle";
import {
  EvaluationStatusBadge,
  EVALUATION_STATUS_OPTIONS as STATUS_OPTIONS,
  type EvaluationStatus,
} from "@/components/EvaluationStatusBadge";

export default function Evaluations() {
  usePageTitle("Avaliações");
  const { user } = useAuth();
  const [, navigate] = useLocation();

  // Filtros existentes
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // V23 — Novos filtros de Canal e Cliente
  const [selectedChannelId, setSelectedChannelId] = useState<string>("0");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("0");

  // Visibilidade por perfil (RBAC frontend)
  const isAdminMaster = user?.role === "admin";
  const isChannelUser =
    user?.role === "channel_admin" || user?.role === "channel_admin_multi" ||
    user?.role === "channel_operator" ||
    user?.role === "channel_consultant";
  const isRHFinal = user?.role === "client_rh";

  // V82b: Consultor Canal não tem acesso ao módulo de Avaliações
  if (user?.role === "channel_consultant") {
    return (
      <SalviaLayout title="Avaliações">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
            <h2 className="text-xl font-semibold text-muted-foreground">Acesso Restrito</h2>
            <p className="text-sm text-muted-foreground">Seu perfil não possui acesso ao módulo de Avaliações.</p>
            <Button variant="outline" onClick={() => navigate("/home/canal")}>Voltar ao Início</Button>
          </div>
        </div>
      </SalviaLayout>
    );
  }

  const showChannelFilter = isAdminMaster;
  const showClientFilter = isAdminMaster || isChannelUser;

  // Mostrar coluna Canal apenas para Admin Sálvia
  const showChannelColumn = isAdminMaster;
  // Mostrar coluna Cliente para Admin Sálvia, Admin Canal, Consultor e Operador do Canal
  const showClientColumn = isAdminMaster || isChannelUser;

  // Listas para os dropdowns
  const { data: channelsList } = trpc.channels.list.useQuery(undefined, {
    enabled: isAdminMaster,
    refetchOnWindowFocus: false,
  });

  // Clientes filtrados pelo canal selecionado (Admin Master) ou pelo canal do usuário (Canal)
  const clientsChannelId = isAdminMaster && selectedChannelId !== "0"
    ? parseInt(selectedChannelId)
    : undefined;
  const { data: clientsList } = trpc.clients.list.useQuery(
    { channelId: clientsChannelId, status: "active", limit: 500 },
    {
      enabled: showClientFilter,
      refetchOnWindowFocus: false,
    }
  );

  // Buscar stats
  const statsQuery = trpc.evaluations.getStats.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  // Buscar pendências com todos os filtros
  const channelIdFilter = isAdminMaster && selectedChannelId !== "0"
    ? parseInt(selectedChannelId)
    : undefined;
  const companyIdFilter = (isAdminMaster || isChannelUser) && selectedCompanyId !== "0"
    ? parseInt(selectedCompanyId)
    : undefined;

  const listQuery = trpc.evaluations.list.useQuery(
    {
      search: searchTerm || undefined,
      status: statusFilter !== "all" ? (statusFilter as EvaluationStatus) : undefined,
      channelId: channelIdFilter,
      companyId: companyIdFilter,
      page: 1,
      pageSize: 100,
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const stats = statsQuery.data || {
    total: 0, pendentes: 0, emAndamento: 0, coletaEncerrada: 0,
    emProcessamento: 0, concluidas: 0, bloqueadasDados: 0,
    bloqueadasRespostas: 0, totalVidas: 0,
  };
  const items = listQuery.data?.items || [];
  const isListLoading = listQuery.isLoading;

  // Options para ComboboxSelect de Canais
  const channelOptions = useMemo(() => [
    { value: "0", label: "Todos os canais" },
    ...(channelsList?.map(ch => ({
      value: ch.id.toString(),
      label: ch.name,
    })) || []),
  ], [channelsList]);

  // Options para ComboboxSelect de Clientes
  const clientOptions = useMemo(() => [
    { value: "0", label: "Todos os clientes" },
    ...((Array.isArray(clientsList) ? clientsList : clientsList?.items)?.map((c: { id: number; name: string; nomeFantasia?: string | null }) => ({
      value: c.id.toString(),
      label: c.nomeFantasia || c.name || "",
    })) || []),
  ], [clientsList]);

  // Verificar se há filtros ativos (além de "all"/"0")
  const hasActiveFilters =
    searchTerm !== "" ||
    statusFilter !== "all" ||
    selectedChannelId !== "0" ||
    selectedCompanyId !== "0";

  // Limpar todos os filtros
  function clearFilters() {
    setSearchTerm("");
    setStatusFilter("all");
    setSelectedChannelId("0");
    setSelectedCompanyId("0");
  }

  // Ao trocar canal, resetar cliente
  function handleChannelChange(value: string) {
    setSelectedChannelId(value);
    setSelectedCompanyId("0");
  }

  const getStatusBadge = (status: string) => <EvaluationStatusBadge status={status} />;

  return (
    <SalviaLayout title="Avaliações">
      <div className="space-y-6">
        {/* Cards de resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 border-l-4 border-l-[#002F59]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold text-[#002F59]">
                  {statsQuery.isLoading ? <Loader2 className="animate-spin h-6 w-6" /> : stats.total}
                </p>
              </div>
              <ClipboardList className="h-8 w-8 text-[#002F59] opacity-30" />
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-amber-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pendentes</p>
                <p className="text-2xl font-bold text-amber-600">
                  {statsQuery.isLoading ? <Loader2 className="animate-spin h-6 w-6" /> : stats.pendentes}
                </p>
              </div>
              <Clock className="h-8 w-8 text-amber-500 opacity-30" />
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Em Andamento</p>
                <p className="text-2xl font-bold text-blue-600">
                  {statsQuery.isLoading ? <Loader2 className="animate-spin h-6 w-6" /> : stats.emAndamento}
                </p>
              </div>
              <PlayCircle className="h-8 w-8 text-blue-500 opacity-30" />
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-emerald-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Concluídas</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {statsQuery.isLoading ? <Loader2 className="animate-spin h-6 w-6" /> : stats.concluidas}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-500 opacity-30" />
            </div>
          </Card>
        </div>

        {/* Tabela de pendências */}
        <Card className="p-6">
          {/* Linha 1: Busca textual + Status */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Buscar por cargo, setor ou cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 items-center">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg text-sm bg-background"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                  Limpar
                </Button>
              )}
            </div>
          </div>

          {/* Linha 2: Filtros de Canal e Cliente (V23 — visibilidade por perfil) */}
          {!isRHFinal && (showChannelFilter || showClientFilter) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {/* Filtro Canal — apenas Admin Master */}
              {showChannelFilter && (
                <ComboboxSelect
                  options={channelOptions}
                  value={selectedChannelId}
                  onValueChange={handleChannelChange}
                  placeholder="Filtrar por canal"
                  searchPlaceholder="Busque pelo nome do canal..."
                  emptyText="Nenhum canal encontrado."
                />
              )}

              {/* Filtro Cliente — Admin Master e usuários de Canal */}
              {showClientFilter && (
                <ComboboxSelect
                  options={clientOptions}
                  value={selectedCompanyId}
                  onValueChange={setSelectedCompanyId}
                  placeholder="Filtrar por cliente"
                  searchPlaceholder="Busque pelo nome do cliente..."
                  emptyText="Nenhum cliente encontrado."
                />
              )}
            </div>
          )}

          {/* Loading state */}
          {isListLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin h-8 w-8 text-[#002F59] mr-3" />
              <span className="text-muted-foreground">Carregando avaliações...</span>
            </div>
          )}

          {/* Error state */}
          {!isListLoading && listQuery.isError && (
            <div className="flex flex-col items-center justify-center py-16 text-red-500">
              <AlertCircle className="h-10 w-10 mb-3" />
              <p className="font-semibold">Erro ao carregar avaliações</p>
              <p className="text-sm text-muted-foreground mt-1">
                {listQuery.error?.message || "Tente novamente mais tarde."}
              </p>
            </div>
          )}

          {/* Empty state */}
          {!isListLoading && !listQuery.isError && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Inbox className="h-12 w-12 mb-3 opacity-40" />
              <p className="font-semibold text-lg">Nenhuma avaliação encontrada</p>
              <p className="text-sm mt-1 max-w-md text-center">
                {hasActiveFilters
                  ? "Nenhum resultado para os filtros aplicados. Tente ajustar a busca."
                  : "As avaliações serão geradas automaticamente ao confirmar uma importação de estrutura organizacional."}
              </p>
            </div>
          )}

          {/* Table */}
          {!isListLoading && !listQuery.isError && items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-[#002F59]">
                    {showChannelColumn && (
                      <th className="text-left py-3 px-4 font-semibold text-[#002F59]">Canal</th>
                    )}
                    {showClientColumn && (
                      <th className="text-left py-3 px-4 font-semibold text-[#002F59]">Cliente</th>
                    )}
                    <th className="text-left py-3 px-4 font-semibold text-[#002F59]">Setor</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#002F59]">Cargo</th>
                    <th className="text-center py-3 px-4 font-semibold text-[#002F59]">Func.</th>
                    <th className="text-center py-3 px-4 font-semibold text-[#002F59]">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-[#002F59]">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50/50 transition-colors">
                      {showChannelColumn && (
                        <td className="py-3 px-4 text-gray-600">{item.channelName}</td>
                      )}
                      {showClientColumn && (
                        <td className="py-3 px-4 text-gray-600">{item.companyName}</td>
                      )}
                      <td className="py-3 px-4 text-gray-600 capitalize">{item.sector}</td>
                      <td className="py-3 px-4 font-medium text-gray-900 capitalize">{item.jobTitle}</td>
                      <td className="py-3 px-4 text-center text-gray-600 font-medium">{item.employeesCount}</td>
                      <td className="py-3 px-4 text-center">{getStatusBadge(item.status)}</td>
                      <td className="py-3 px-4 text-center">
                        {item.status === "AVALIACAO_CONCLUIDA" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => navigate(`/evaluations/${item.id}/resultado`)}
                          >
                            <Eye className="h-3 w-3" />
                            Ver Resultado
                          </Button>
                        )}
                        {item.status === "AVALIACAO_EM_PROCESSAMENTO" && (
                          <span className="text-xs text-purple-500 italic flex items-center justify-center gap-1">
                            <Cog className="h-3 w-3 animate-spin" />
                            Processando...
                          </span>
                        )}
                        {(item.status === "AVALIACAO_BLOQUEADA_DADOS_INSUFICIENTES" || item.status === "AVALIACAO_BLOQUEADA_SEM_RESPOSTAS") && (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Rodapé com total */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t text-sm text-muted-foreground">
                <span>
                  Exibindo {items.length} de {listQuery.data?.total || 0} avaliações
                </span>
                {hasActiveFilters && (
                  <span className="text-xs text-blue-600 font-medium">
                    Filtros ativos
                  </span>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </SalviaLayout>
  );
}
